<template>
  <div class="widget-postal-field">
    <template v-if="isTouchLike">
      <el-input
        :model-value="modelValue"
        :placeholder="placeholder"
        :size="size"
        class="widget-postal-input"
        clearable
        inputmode="numeric"
        autocomplete="postal-code"
        @update:model-value="handleInput"
        @clear="handleClear"
        @blur="handleBlur"
      />
      <ul
        v-if="showSuggestions"
        class="widget-postal-suggestions"
        role="listbox"
        :aria-label="ariaLabel"
      >
        <li v-if="isLoading" class="widget-postal-suggestions-status">Buscando…</li>
        <li
          v-else-if="searchStatus === 'error'"
          class="widget-postal-suggestions-status widget-postal-suggestions-error"
        >
          <span>{{ statusMessage }}</span>
          <button type="button" class="widget-postal-retry" @mousedown.prevent="retrySearch">
            Reintentar
          </button>
        </li>
        <li
          v-else-if="searchStatus === 'empty' && queryLength >= minQueryLength"
          class="widget-postal-suggestions-status"
        >
          {{ statusMessage || 'Sin coincidencias para este código postal.' }}
        </li>
        <li
          v-for="item in suggestions"
          :key="`${item.code}-${item.location}`"
          role="option"
          class="widget-postal-suggestion-item"
          @mousedown.prevent="handleSelect(item)"
        >
          <div class="postal-code-option">
            <span class="postal-code-value">{{ item.code }}</span>
            <span class="postal-code-location">{{ item.location }}</span>
          </div>
        </li>
      </ul>
    </template>

    <template v-else>
      <el-autocomplete
        :model-value="modelValue"
        :fetch-suggestions="fetchAutocomplete"
        :placeholder="placeholder"
        :size="size"
        value-key="displayValue"
        class="widget-postal-input"
        clearable
        teleported
        popper-class="widget-autocomplete-dropdown premium-dropdown"
        :popper-options="autocompletePopperOptions"
        @select="handleSelect"
        @clear="handleClear"
        @update:model-value="handleInput"
      >
        <template #default="{ item }">
          <div class="postal-code-option">
            <span class="postal-code-value">{{ item.code }}</span>
            <span class="postal-code-location">{{ item.location }}</span>
          </div>
        </template>
      </el-autocomplete>
      <p
        v-if="desktopStatusVisible"
        class="widget-postal-desktop-status"
        :class="{ 'is-error': searchStatus === 'error' }"
        role="status"
      >
        <span>{{ statusMessage }}</span>
        <button
          v-if="searchStatus === 'error'"
          type="button"
          class="widget-postal-retry"
          @click="retrySearch"
        >
          Reintentar
        </button>
      </p>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { useTouchLikeDevice } from '@/composables/useTouchLikeDevice';
import { usePostalCode, type PostalCodeOption } from '@/composables/usePostalCode';
import type { PostalCodeSearchStatus } from '@/composables/postalCodeProvider';

interface Props {
  modelValue: string;
  placeholder?: string;
  size?: 'default' | 'large';
  ariaLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Ej: 03810',
  size: 'large',
  ariaLabel: 'Sugerencias de código postal',
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
  select: [item: PostalCodeOption];
  clear: [];
  invalidate: [];
}>();

const minQueryLength = 2;
const { isTouchLike } = useTouchLikeDevice();
const { searchPostalCodesDetailed } = usePostalCode();

const suggestions = ref<PostalCodeOption[]>([]);
const isLoading = ref(false);
const isSuggestionsOpen = ref(false);
const searchStatus = ref<PostalCodeSearchStatus | 'idle'>('idle');
const statusMessage = ref('');
const requestSeq = ref(0);
let abortController: AbortController | null = null;
/** Prevents select→model update from clearing the chosen code. */
let suppressInvalidate = false;

const autocompletePopperOptions = {
  placement: 'bottom-start' as const,
  strategy: 'fixed' as const,
  modifiers: [
    { name: 'preventOverflow', options: { boundary: 'viewport', padding: 8 } },
    { name: 'flip', options: { fallbackPlacements: ['top-start', 'bottom-start', 'bottom', 'top'] } },
  ],
};

const queryLength = computed(() => props.modelValue.trim().length);

const showSuggestions = computed(
  () => isSuggestionsOpen.value && queryLength.value >= minQueryLength,
);

const desktopStatusVisible = computed(
  () =>
    !isLoading.value
    && queryLength.value >= minQueryLength
    && (searchStatus.value === 'empty' || searchStatus.value === 'error'),
);

const cancelInFlight = (): void => {
  abortController?.abort();
  abortController = null;
};

const fetchSuggestions = async (query: string): Promise<PostalCodeOption[]> => {
  const trimmed = query.trim();
  if (trimmed.length < minQueryLength) {
    suggestions.value = [];
    isLoading.value = false;
    searchStatus.value = 'idle';
    statusMessage.value = '';
    return [];
  }

  cancelInFlight();
  const controller = new AbortController();
  abortController = controller;
  const seq = ++requestSeq.value;
  isLoading.value = true;
  searchStatus.value = 'idle';
  statusMessage.value = '';

  try {
    const outcome = await searchPostalCodesDetailed(trimmed, { signal: controller.signal });
    if (seq !== requestSeq.value) return [];

    suggestions.value = outcome.items;
    searchStatus.value = outcome.status;
    statusMessage.value = outcome.message ?? '';
    isLoading.value = false;
    return outcome.items;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return [];
    }
    if (seq !== requestSeq.value) return [];
    suggestions.value = [];
    searchStatus.value = 'error';
    statusMessage.value = 'No se pudo buscar el código postal. Intenta de nuevo.';
    isLoading.value = false;
    return [];
  }
};

const debouncedFetch = useDebounceFn((query: string) => {
  void fetchSuggestions(query);
}, 300);

const fetchAutocomplete = (
  queryString: string,
  cb: (results: PostalCodeOption[]) => void,
): void => {
  void fetchSuggestions(queryString).then((items) => {
    cb(items);
  });
};

const retrySearch = (): void => {
  void fetchSuggestions(props.modelValue);
};

const handleInput = (value: string): void => {
  emit('update:modelValue', value);
  if (suppressInvalidate) {
    return;
  }
  emit('invalidate');
  isSuggestionsOpen.value = true;
  void debouncedFetch(value);
};

const handleSelect = (item: PostalCodeOption): void => {
  suppressInvalidate = true;
  emit('select', item);
  emit('update:modelValue', item.displayValue);
  isSuggestionsOpen.value = false;
  suggestions.value = [];
  searchStatus.value = 'ok';
  statusMessage.value = '';
  queueMicrotask(() => {
    suppressInvalidate = false;
  });
};

const handleClear = (): void => {
  cancelInFlight();
  emit('clear');
  emit('update:modelValue', '');
  suggestions.value = [];
  isSuggestionsOpen.value = false;
  searchStatus.value = 'idle';
  statusMessage.value = '';
};

const handleBlur = (): void => {
  window.setTimeout(() => {
    isSuggestionsOpen.value = false;
  }, 150);
};

watch(
  () => props.modelValue,
  (value) => {
    if (!value.trim()) {
      suggestions.value = [];
      isSuggestionsOpen.value = false;
      searchStatus.value = 'idle';
      statusMessage.value = '';
    }
  },
);
</script>

<style scoped>
.widget-postal-field {
  position: relative;
  z-index: 30;
  width: 100%;
}

.widget-postal-suggestions {
  margin: 8px 0 0;
  padding: 6px;
  list-style: none;
  max-height: min(220px, 40vh);
  overflow-y: auto;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  box-shadow: 0 12px 28px -12px rgba(15, 23, 42, 0.28);
}

.widget-postal-suggestion-item {
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.widget-postal-suggestion-item:active,
.widget-postal-suggestion-item:hover {
  background: var(--ui-accent-soft);
}

.widget-postal-suggestions-status {
  padding: 12px 14px;
  font-size: 13px;
  color: var(--ui-muted);
}

.widget-postal-suggestions-error {
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: var(--ui-danger, #b91c1c);
}

.widget-postal-desktop-status {
  margin: 6px 0 0;
  font-size: 12px;
  color: var(--ui-muted);
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.widget-postal-desktop-status.is-error {
  color: var(--ui-danger, #b91c1c);
}

.widget-postal-retry {
  border: 1px solid currentColor;
  background: transparent;
  color: inherit;
  border-radius: 8px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.widget-postal-retry:hover {
  opacity: 0.85;
}

.postal-code-option {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 12px;
}

.postal-code-value {
  font-weight: 700;
  color: var(--ui-strong);
  font-size: 15px;
}

.postal-code-location {
  font-size: 12px;
  color: var(--ui-muted);
  line-height: 1.35;
  word-break: break-word;
}

.widget-postal-input :deep(.el-input__wrapper) {
  background: #f8fafc;
  min-height: 48px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: none !important;
  transition: all 0.3s ease;
  padding: 0 16px;
}

.widget-postal-input :deep(.el-input__inner) {
  font-size: 15px;
  font-weight: 500;
  color: var(--ui-strong);
}

.widget-postal-input :deep(.el-input__wrapper:hover) {
  border-color: #cbd5e1;
  background: #ffffff;
}

.widget-postal-input :deep(.el-input__wrapper.is-focus) {
  background: #ffffff;
  border-color: var(--ui-accent);
  box-shadow: 0 0 0 4px var(--ui-accent-soft) !important;
}
</style>
